import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

interface CachedResolvedUrl {
  url: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  private readonly apiUrl: string = environment.apiUrl;

  /**
   * How long a resolved file URL is considered fresh, in milliseconds.
   * Backend pre-signed URLs typically remain valid much longer than this,
   * but we keep the cache window conservative so an occasional stale URL
   * is refreshed before it actually expires.
   */
  private static readonly CACHE_TTL_MS = 15 * 60 * 1000;

  private readonly resolvedUrlCache = new Map<string, CachedResolvedUrl>();
  private readonly inFlightRequests = new Map<string, Observable<string>>();

  constructor(private readonly _http: HttpClient) {}

  /**
   * Resolves a file name to a downloadable URL.
   *
   * Caching:
   * - The first call for a given `fileName` hits the backend.
   * - Concurrent callers share the same in-flight HTTP request.
   * - Subsequent calls within {@link CACHE_TTL_MS} return the cached URL
   *   synchronously (wrapped in `of`), avoiding any network round-trip.
   * - Expired entries are evicted on access and refetched.
   */
  public getFileDownloadUrl(fileName: string): Observable<string> {
    if (!fileName) {
      return of('');
    }

    const cached = this.resolvedUrlCache.get(fileName);
    if (cached && cached.expiresAt > Date.now()) {
      return of(cached.url);
    }

    if (cached) {
      // Expired — drop it before issuing a fresh request.
      this.resolvedUrlCache.delete(fileName);
    }

    const inFlight = this.inFlightRequests.get(fileName);
    if (inFlight) {
      return inFlight;
    }

    const request$ = this._http
      .get<{ result: string }>(`${environment.fileDownloadUrl}${fileName}`)
      .pipe(
        map((res) => res.result),
        tap((url) => {
          this.resolvedUrlCache.set(fileName, {
            url,
            expiresAt: Date.now() + FilesService.CACHE_TTL_MS,
          });
        }),
        catchError((error: Error) => throwError(() => error)),
        finalize(() => {
          this.inFlightRequests.delete(fileName);
        }),
        shareReplay(1)
      );

    this.inFlightRequests.set(fileName, request$);
    return request$;
  }

  /**
   * Drop a single file from the cache. Use after the backend regenerates
   * the file (e.g. after an upload that overwrites it).
   */
  public invalidateFile(fileName: string): void {
    this.resolvedUrlCache.delete(fileName);
    this.inFlightRequests.delete(fileName);
  }

  /** Drop every cached resolved URL. */
  public clearFileCache(): void {
    this.resolvedUrlCache.clear();
    this.inFlightRequests.clear();
  }

  /**
   * Hits a specific endpoint to retrieve a file or image URL dynamically.
   * @param data The payload to send to the endpoint (e.g., { fileName: '...' })
   * @returns Observable with the extracted URL string
   */
  public getImageUrl(data: any): Observable<string> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .post<any>(`${this.apiUrl}/files/opn/v1/get-image`, data, httpOptions)
      .pipe(
        map((response) => response.body.result),
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }
}
